import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';

// =====================
// Mutation Hooks
// =====================

/**
 * Create a new reply to a post
 */
export const useCreateReply = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async replyData => {
      const response = await axios.post(`${HOST}/api/certification-tool/replies`, replyData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate the post to refresh replies
      if (variables.post_Id) {
        queryClient.invalidateQueries({
          queryKey: ['posts', variables.post_Id],
        });
      }
      // Invalidate posts list
      queryClient.invalidateQueries({
        queryKey: ['posts', 'submissionDetail'],
      });
      // Invalidate submission to refresh replies in submission details
      if (submissionId) {
        queryClient.invalidateQueries({
          queryKey: ['submission', submissionId],
        });
      }
    },
  });
};

/**
 * Update an existing reply
 */
export const useUpdateReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ replyId, patchDocument }) => {
      const response = await axios.patch(`${HOST}/api/certification-tool/replies/${replyId}`, patchDocument);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all post queries to refresh replies
      queryClient.invalidateQueries({
        queryKey: ['posts'],
      });
    },
  });
};

/**
 * Delete a reply
 */
export const useDeleteReply = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async replyId => {
      const response = await axios.delete(`${HOST}/api/certification-tool/replies/${replyId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all post queries
      queryClient.invalidateQueries({
        queryKey: ['posts'],
      });
      // Invalidate submission to refresh in UI
      if (submissionId) {
        queryClient.invalidateQueries({
          queryKey: ['submission', submissionId],
        });
      }
    },
  });
};

/**
 * Create a reaction on a reply
 */
export const useCreateReplyReaction = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async reactionData => {
      const response = await axios.post(`${HOST}/api/certification-tool/reply-reactions`, reactionData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all post queries to refresh reactions
      queryClient.invalidateQueries({
        queryKey: ['posts'],
      });
      // Invalidate submission to refresh in UI
      if (submissionId) {
        queryClient.invalidateQueries({
          queryKey: ['submission', submissionId],
        });
      }
    },
  });
};

/**
 * Delete a reply reaction
 */
export const useDeleteReplyReaction = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async reactionId => {
      const response = await axios.delete(`${HOST}/api/certification-tool/reply-reactions/${reactionId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all post queries to refresh reactions
      queryClient.invalidateQueries({
        queryKey: ['posts'],
      });
      // Invalidate submission to refresh in UI
      if (submissionId) {
        queryClient.invalidateQueries({
          queryKey: ['submission', submissionId],
        });
      }
    },
  });
};
